import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Check, Lock } from "lucide-react";
import { logo } from "../config";
import LanguageSwitcher from "./LanguageSwitcher";

const KEY = "ao2_privacy_consent";

export default function ConsentGate() {
  const [status, setStatus] = useState("checking"); // checking | show | declined | done
  const [checked, setChecked] = useState(false);
  const { pathname } = useLocation();
  const onPrivacyPage = pathname === "/privacy";

  useEffect(() => {
    const saved = localStorage.getItem(KEY);
    setStatus(saved === "agreed" ? "done" : "show");
  }, []);

  useEffect(() => {
    const block = (status === "show" || status === "declined") && !onPrivacyPage;
    document.body.style.overflow = block ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [status, onPrivacyPage]);

  const agree = () => {
    localStorage.setItem(KEY, "agreed");
    setStatus("done");
  };

  // Always let visitors read the Privacy Policy — never overlay it.
  if (status === "checking" || status === "done" || onPrivacyPage) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-5 grain"
        style={{ background: "rgba(3,3,3,0.88)", backdropFilter: "blur(10px)" }}
        data-testid="consent-gate"
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-lg rounded-2xl border border-gold/25 bg-ink-surface/90 p-7 md:p-9 shadow-2xl"
        >
          <div className="flex items-center gap-3">
            <img src={logo} alt="Arco Orbis2" className="h-11 w-11 object-contain" />
            <span className="notranslate font-display font-medium tracking-[0.25em] text-sm" translate="no">ARCO <span className="gold-text">ORBIS2</span></span>
            <div className="ms-auto notranslate" translate="no">
              <LanguageSwitcher testId="consent-lang" />
            </div>
          </div>

          {status === "show" ? (
            <>
              <div className="overline text-gold mt-7 mb-3 flex items-center gap-2">
                <ShieldCheck size={15} /> Your Privacy
              </div>
              <h2 className="font-display font-light text-2xl md:text-3xl text-white leading-snug">
                We respect your privacy.
              </h2>
              <p className="mt-4 text-silver/70 font-body font-light text-sm leading-relaxed">
                Before continuing, please review and agree to our{" "}
                <Link to="/privacy" className="text-gold hover:underline" data-testid="consent-privacy-link">Privacy Policy</Link>.
                It explains what information we collect (such as enquiry and chat details), how we use it, and your rights.
                We never sell your data or use advertising trackers.
              </p>

              <button
                type="button"
                onClick={() => setChecked((v) => !v)}
                className="mt-6 w-full flex items-start gap-3 text-left rounded-xl border border-white/10 bg-ink/60 p-4 hover:border-gold/40 transition-colors"
                data-testid="consent-checkbox"
                aria-pressed={checked}
              >
                <span className={`mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${checked ? "bg-gold border-gold text-black" : "border-silver/40 text-transparent"}`}>
                  <Check size={14} strokeWidth={3} />
                </span>
                <span className="font-body text-sm text-silver/85">
                  I have read and agree to the Privacy Policy and the processing of my information as described.
                </span>
              </button>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={agree}
                  disabled={!checked}
                  className="gold-sweep flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black px-6 py-3.5 text-sm font-body font-medium hover:bg-gold-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="consent-agree"
                >
                  Agree & Continue
                </button>
                <button
                  onClick={() => setStatus("declined")}
                  className="flex-1 inline-flex items-center justify-center rounded-full border border-silver/30 px-6 py-3.5 text-sm font-body text-silver hover:border-silver hover:text-white transition-colors"
                  data-testid="consent-decline"
                >
                  Decline
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mt-7 flex items-center gap-2 text-silver/60">
                <Lock size={16} className="text-gold" />
                <span className="overline text-silver/60">Access Paused</span>
              </div>
              <h2 className="mt-3 font-display font-light text-2xl md:text-3xl text-white leading-snug">
                Agreement required to continue.
              </h2>
              <p className="mt-4 text-silver/70 font-body font-light text-sm leading-relaxed">
                To use this website, we need your agreement to our Privacy Policy. You're welcome to review it in full,
                then accept to continue. If you prefer not to agree, you may simply close this tab.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => { setChecked(false); setStatus("show"); }}
                  className="gold-sweep flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black px-6 py-3.5 text-sm font-body font-medium hover:bg-gold-light transition-colors"
                  data-testid="consent-reconsider"
                >
                  Review & Agree
                </button>
                <Link
                  to="/privacy"
                  className="flex-1 inline-flex items-center justify-center rounded-full border border-silver/30 px-6 py-3.5 text-sm font-body text-silver hover:border-silver hover:text-white transition-colors"
                  data-testid="consent-read-policy"
                >
                  Read Privacy Policy
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
